import { type StateCreator } from 'zustand';

/**
 * Per-account intro memory on this browser. `watched` is set when the intro finishes or is skipped.
 * The review task stays archived across a replay, so clearing `watched` must not drop the flag.
 * `hasSeenIntro` stays set after the first finish. Replay clears `watched`, which would otherwise
 * look like a first watch — the case where Skip intro stays hidden.
 */
export type WorkspaceAgentIntroAccountRecord = {
  watched: boolean;
  isReviewTaskArchived: boolean;
  hasSeenIntro: boolean;
};

export type WorkspaceAgentIntroByAccountId = Record<string, WorkspaceAgentIntroAccountRecord>;

export type WorkspaceAgentOnboardingReviewTaskVisibility = 'active' | 'archived' | 'hidden';

export const getWorkspaceAgentOnboardingReviewTaskVisibility = (
  workspaceAgentIntro: WorkspaceAgentIntroByAccountId,
  accountId: string | undefined,
): WorkspaceAgentOnboardingReviewTaskVisibility => {
  if (!accountId) {
    return 'hidden';
  }

  const record = workspaceAgentIntro[accountId];

  if (!record) {
    return 'hidden';
  }

  if (record.isReviewTaskArchived) {
    return 'archived';
  }

  if (record.watched) {
    return 'active';
  }

  return 'hidden';
};

export type WorkspaceAgentIntroSlice = {
  workspaceAgentIntro: WorkspaceAgentIntroByAccountId;
  markWorkspaceAgentIntroComplete: (accountId: string) => void;
  clearWorkspaceAgentIntroComplete: (accountId: string) => void;
  archiveWorkspaceAgentIntroReviewTask: (accountId: string) => void;
};

export const createWorkspaceAgentIntroSlice: StateCreator<
  WorkspaceAgentIntroSlice,
  [],
  [],
  WorkspaceAgentIntroSlice
> = (set) => ({
  workspaceAgentIntro: {},
  markWorkspaceAgentIntroComplete: (accountId) =>
    set((state) => ({
      workspaceAgentIntro: {
        ...state.workspaceAgentIntro,
        [accountId]: {
          watched: true,
          isReviewTaskArchived: state.workspaceAgentIntro[accountId]?.isReviewTaskArchived ?? false,
          hasSeenIntro: true,
        },
      },
    })),
  clearWorkspaceAgentIntroComplete: (accountId) =>
    set((state) => {
      const currentRecord = state.workspaceAgentIntro[accountId];

      if (!currentRecord) {
        return state;
      }

      return {
        workspaceAgentIntro: {
          ...state.workspaceAgentIntro,
          [accountId]: {
            watched: false,
            isReviewTaskArchived: currentRecord.isReviewTaskArchived,
            hasSeenIntro: true,
          },
        },
      };
    }),
  archiveWorkspaceAgentIntroReviewTask: (accountId) =>
    set((state) => {
      const currentRecord = state.workspaceAgentIntro[accountId];

      return {
        workspaceAgentIntro: {
          ...state.workspaceAgentIntro,
          [accountId]: {
            watched: currentRecord?.watched ?? true,
            isReviewTaskArchived: true,
            hasSeenIntro: true,
          },
        },
      };
    }),
});
